import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Grid, Card, CardContent, LinearProgress, Alert, Chip, Avatar, IconButton, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { ArrowBack, Memory, Build } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';

const statusColor: any = { RUNNING: 'success', IDLE: 'default', DOWN: 'error', MAINTENANCE: 'warning', OFFLINE: 'default' };
const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Card variant="outlined" sx={{ height: '100%' }}><CardContent sx={{ py: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
  </CardContent></Card>
);

const Machine360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { financeWorkspaceService.machine360(Number(id)).then(setD).catch((e) => setError(e.message)).finally(() => setLoading(false)); }, [id]);
  if (loading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (error || !d) return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Not found'}</Alert></Box>;
  const m = d.metrics || {}; const p = d.profile;
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Avatar sx={{ bgcolor: 'warning.main' }}><Memory /></Avatar>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{p.name}</Typography>
            <Chip size="small" label={p.status} color={statusColor[p.status] || 'default'} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <span style={{ fontFamily: 'monospace' }}>{p.code}</span> · Capacity {p.capacity ? `${p.capacity}/hr` : '—'}{p.commissionedOn ? ` · Commissioned ${String(p.commissionedOn).slice(0, 10)}` : ''}
          </Typography>
        </Box>
        {(p.status === 'DOWN' || p.status === 'MAINTENANCE') && <Button variant="contained" startIcon={<Build />} onClick={() => navigate(`/manufacturing/maintenance/equipment/${id}`)}>Maintenance Order</Button>}
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Produced" value={m.produced ?? 0} color="success.main" /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Rejected" value={m.rejected ?? 0} color="error.main" /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Runtime" value={`${m.runtimeMins ?? 0} min`} /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Downtime" value={`${m.downtimeMins ?? 0} min`} color="warning.main" /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Utilisation" value={m.utilisation != null ? `${m.utilisation}%` : '—'} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Work Orders</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Order</TableCell><TableCell>Status</TableCell><TableCell align="right">Planned</TableCell><TableCell align="right">Produced</TableCell></TableRow></TableHead>
              <TableBody>
                {d.workOrders.map((w: any, i: number) => (
                  <TableRow key={i} hover sx={{ cursor: w.id ? 'pointer' : 'default' }} onClick={w.id ? () => navigate(`/manufacturing/work-orders/${w.id}`) : undefined}><TableCell sx={{ fontFamily: 'monospace', color: w.id ? 'primary.main' : 'inherit' }}>{w.ref}</TableCell><TableCell><Chip size="small" label={w.status.replace('_', ' ')} /></TableCell><TableCell align="right">{w.planned}</TableCell><TableCell align="right">{w.produced}</TableCell></TableRow>
                ))}
                {!d.workOrders.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No work orders.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Downtime History</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Reason</TableCell><TableCell>Since</TableCell><TableCell align="right">Min</TableCell></TableRow></TableHead>
              <TableBody>
                {d.downtime.map((x: any, i: number) => (
                  <TableRow key={i} hover><TableCell>{x.reason}</TableCell><TableCell>{String(x.from).slice(0, 16).replace('T', ' ')}</TableCell><TableCell align="right">{x.mins ?? '—'}</TableCell></TableRow>
                ))}
                {!d.downtime.length && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No downtime recorded.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Machine360Page;
